/**
 * Daemon-published materialized run state (sealed `run.snapshot`s), authenticated here.
 *
 * List surfaces read these instead of event histories: one batched hub read for many runs, then
 * live pushes as daemons publish. The session viewer seeds its store from the newest snapshot and
 * applies only the events after it.
 *
 * Trust: AEAD under the account data key binds a snapshot to its account, run, covered seq and
 * kind (a relay cannot read, edit, or move it). A relay can still serve an older authentic
 * snapshot (rollback) or withhold one: the newest seq and epoch accepted per run are pinned, and
 * anything older is refused; a stale snapshot only costs a longer event tail, never a wrong state.
 */
import { type ContentBox, type ContentContext, fromUtf8, openContent } from '@viko/crypto'
import { RUN_SNAPSHOT_BATCH, RunSnapshotContent, RunSnapshotPlaintext } from '@viko/protocol'
import { materialFromJson, type RunMaterial } from '@viko/transcript-ui'

export interface OpenedSnapshot {
  runId: string
  seq: number
  epoch: number
  material: RunMaterial
}

/** Authenticate one sealed snapshot; throws on any mismatch (never a partial result). */
export async function openRunSnapshot(
  keyForEpoch: (epoch: number) => Promise<CryptoKey>,
  accountId: string,
  raw: unknown,
  runId?: string,
): Promise<OpenedSnapshot> {
  const s = RunSnapshotContent.parse(raw)
  const c = s.context
  if (c.accountId !== accountId || (runId !== undefined && c.runId !== runId))
    throw new Error('wrong snapshot context')
  const plain = await openContent(
    await keyForEpoch(c.epoch),
    c as ContentContext,
    s.box as ContentBox,
  )
  const p = RunSnapshotPlaintext.parse(JSON.parse(fromUtf8(plain)))
  if (p.runId !== c.runId || p.seq !== c.seq) throw new Error('snapshot envelope mismatch')
  const material = materialFromJson(p.material)
  if (
    material.header.lastSeq !== c.seq ||
    (material.header.runId && material.header.runId !== c.runId)
  )
    throw new Error('snapshot material mismatch')
  return { runId: c.runId, seq: c.seq, epoch: c.epoch, material }
}

export interface RunSnapshotsOptions {
  accountId: string
  keyForEpoch: (epoch: number) => Promise<CryptoKey>
  /** `content.snapshot.get` (ciphertext + the hub's head per run). */
  request: (
    runIds: readonly string[],
  ) => Promise<{ snapshots: unknown[]; heads: Record<string, number> }>
}

/** Per-account snapshot index: batched reads, pushes, rollback pins. */
export class RunSnapshots {
  private locked = false
  private readonly latest = new Map<string, OpenedSnapshot>()
  private readonly heads = new Map<string, number>()
  private readonly listeners = new Set<(s: OpenedSnapshot) => void>()
  private readonly inflight = new Map<string, Promise<void>>()
  private queued = new Set<string>()
  private flushing: Promise<void> | undefined
  /** Runs asked recently (a missing snapshot is not re-requested on every list render). */
  private readonly asked = new Map<string, number>()

  constructor(private readonly o: RunSnapshotsOptions) {}

  get accountId() {
    return this.o.accountId
  }

  lock() {
    this.locked = true
    this.latest.clear()
    this.listeners.clear()
  }

  get(runId: string): OpenedSnapshot | undefined {
    return this.latest.get(runId)
  }

  /** The hub's head seq for a run, from the last batch read. */
  head(runId: string): number | undefined {
    return this.heads.get(runId)
  }

  subscribe(cb: (s: OpenedSnapshot) => void) {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  /**
   * Pin an authenticated state known locally (sealed cache, or a store that applied events up to
   * `seq`): an older snapshot is then a rollback and is refused.
   */
  pin(runId: string, seq: number, epoch = 1) {
    const cur = this.pins.get(runId)
    if (!cur || seq > cur.seq)
      this.pins.set(runId, { seq, epoch: Math.max(epoch, cur?.epoch ?? 1) })
  }
  private readonly pins = new Map<string, { seq: number; epoch: number }>()

  /** Accept an authenticated snapshot unless it is older than what this device already holds. */
  accept(s: OpenedSnapshot): boolean {
    if (this.locked) return false
    const cur = this.latest.get(s.runId)
    const pin = this.pins.get(s.runId)
    if (cur && (s.seq < cur.seq || s.epoch < cur.epoch)) return false
    if (pin && (s.seq < pin.seq || s.epoch < pin.epoch)) return false
    if (cur && s.seq === cur.seq) return false
    this.latest.set(s.runId, s)
    this.pin(s.runId, s.seq, s.epoch)
    for (const l of this.listeners) l(s)
    return true
  }

  /** A pushed ciphertext snapshot. Invalid pushes are dropped silently (never trusted). */
  async push(raw: unknown): Promise<OpenedSnapshot | undefined> {
    if (this.locked) return undefined
    try {
      const s = await openRunSnapshot(this.o.keyForEpoch, this.o.accountId, raw)
      return this.accept(s) ? s : undefined
    } catch {
      return undefined
    }
  }

  /** Fetch (batched, ≤64 runs per hub read) the newest snapshot of each run. */
  fetch(runIds: readonly string[], o: { force?: boolean } = {}): Promise<void> {
    if (this.locked) return Promise.resolve()
    const now = Date.now()
    const waits: Promise<void>[] = []
    for (const id of runIds) {
      const pending = this.inflight.get(id)
      if (pending) {
        waits.push(pending)
        continue
      }
      if (!o.force && now - (this.asked.get(id) ?? 0) < 30_000) continue
      this.asked.set(id, now)
      this.queued.add(id)
    }
    if (this.queued.size) {
      // Coalesce every request made in this tick into one hub read.
      this.flushing ??= Promise.resolve().then(() => this.flush())
      const flushing = this.flushing
      for (const id of this.queued) this.inflight.set(id, flushing)
      waits.push(flushing)
    }
    return Promise.all(waits).then(() => {})
  }

  private async flush() {
    const ids = [...this.queued]
    this.queued = new Set()
    this.flushing = undefined
    try {
      for (let i = 0; i < ids.length; i += RUN_SNAPSHOT_BATCH) {
        const chunk = ids.slice(i, i + RUN_SNAPSHOT_BATCH)
        const out = await this.o.request(chunk).catch(() => ({ snapshots: [], heads: {} }))
        if (this.locked) return
        for (const [id, h] of Object.entries(out.heads))
          if (chunk.includes(id)) this.heads.set(id, h)
        const opened = await Promise.all(
          out.snapshots.map((raw) =>
            openRunSnapshot(this.o.keyForEpoch, this.o.accountId, raw).catch(() => undefined),
          ),
        )
        for (const s of opened) if (s && chunk.includes(s.runId)) this.accept(s)
      }
    } finally {
      for (const id of ids) this.inflight.delete(id)
    }
  }
}
